import type { StateOperator } from '@ngxs/store';
import { AggregationFunction, type Control, type StoreControl, type TelemetryDataModel, type TelemetryDataValues, TimeUnit } from 'commons';
import { db } from './app/db';

/**
 * Returns a function that takes a control and returns true if the control id does NOT match the id passed in.
 * @param id
 * @returns
 */
export const isNotSameControlAsId =
  (id: string | number) =>
  ({ id: remove_id }: { id: string | number }) =>
    remove_id !== id;

/**
 *
 * @param actionControl
 * @returns
 */
export const toggleIsHidden = ({ id }: StoreControl) => {
  return (control: StoreControl) => ({
    ...control,
    isHidden: id === control.id ? !control.isHidden : control.isHidden,
  });
};
/**
 *
 * @param param0
 * @param field
 * @returns
 */
export const toggleControlField = ({ id }: StoreControl, field: 'isHidden' | 'confidenceBand') => {
  return (control: StoreControl) => ({
    ...control,
    [field]: id === control.id ? !control[field] : control[field],
  });
};
/**
 *
 * @param param0
 * @param field
 * @param value
 * @returns
 */
export const setControlField = ({ id }: StoreControl, field: 'isHidden' | 'confidenceBand', value: boolean) => {
  return (control: StoreControl) => ({
    ...control,
    [field]: id === control.id ? value : control[field],
  });
};
/**
 *
 * @param predicate
 * @returns
 */
export const removeItems = <T>(predicate: (item: T) => boolean): StateOperator<T[]> => {
  return (existingState: Readonly<T[]>): T[] => {
    return existingState?.filter((item) => !predicate(item));
  };
};
/**
 *
 * @param idSelector
 * @param item
 * @returns
 */
export const upsertItem = <T>(idSelector: (item: T) => string, item: T): StateOperator<T[]> => {
  return (existingState: Readonly<T[]>): T[] => {
    const index = existingState.findIndex((existingItem) => idSelector(existingItem) === idSelector(item));

    if (index >= 0) {
      // Wenn das Element gefunden wird, aktualisiere es
      return [...existingState.slice(0, index), item, ...existingState.slice(index + 1)];
    }
    // Wenn das Element nicht gefunden wird, füge es hinzu
    else {
      return [...existingState, item];
    }
  };
};
export const upsertItems = <T>(idSelector: (item: T) => string | number, items: T[]): StateOperator<T[]> => {
  return (existingItems: Readonly<T[]>): T[] => {
    const updatedItems = [...existingItems];

    const newItems =
      items?.filter((item) => {
        return !existingItems.some((existingItem) => idSelector(existingItem) === idSelector(item));
      }) || [];

    return [...updatedItems, ...newItems].map((d) => ({ ...d }));
  };
};
/**
 * add all
 * @param entity
 * @returns
 */
export const addBatchAndDistinct = <TState extends Record<string, unknown>, TEntity>(entities: TEntity[], field: keyof TState): StateOperator<TState> => {
  return (state: Readonly<TState>) => {
    const existingValues = (state[field] as TEntity[] | undefined) ?? [];
    const fieldValue = [...new Set([...existingValues, ...entities])].filter((d) => !!d);
    return {
      ...state,
      [field]: fieldValue,
    };
  };
};
/**
 *
 * @param arr
 * @param laneIndex
 * @param i
 * @returns
 */
export const filterLanesByControl = (control: string) => (arr: StoreControl[] | Control[], id: string, laneId?: string) =>
  laneId === undefined || id === laneId ? arr.filter(isNotSameControlAsId(control)) : arr;

/**
 *
 * @param ctx
 * @param id
 * @returns
 */
export const addToIndexedDB = (id: string) => {
  return (telemetry: TelemetryDataModel) => {
    const mappingKey = getMappingKey(id, telemetry);
    const keyValues: TelemetryDataValues[] = telemetry.channelValues?.flatMap((d) => d.values) ?? [];

    return db.channelData.put({ timeseries: keyValues, uuid: mappingKey }, mappingKey);
  };
};

export const findbyId =
  <T extends { id: string }>({ id }: T) =>
  (a: T) =>
    a.id === id;

/**
 * returns a string contaiagne the datasource id, window size, unit and agg funtion to group groupable channel
 * @param entity
 * @param telemetry
 * @returns
 */
export const getMappingKey = (id: string, telemetry?: TelemetryDataModel) => {
  if (telemetry) {
    return `${id}_${telemetry.windowSize}_${telemetry.windowUnit}_${telemetry.aggregationMethod}`;
  }

  return `${id}_${1}_${TimeUnit[TimeUnit.Day]}_${AggregationFunction[AggregationFunction.Avg]}`;
};
